import {
    Box,
    Button,
    Checkbox,
    Icon,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import { MouseEvent, useEffect, useState } from 'react';
import useOptions from '../../hooks/useOptions';
import PageHeader from '../../materio/@core/components/page-header';
import ApplicationStore from '../../stores/ApplicationStore';
import { ApiResponseType } from '../../types';
import { RoleRecordPermissionDto } from '../../types/common';
import { client } from '../../utils/axios-client';
import { ClaimType } from '../common/RenderIf';
import SimpleDialog from '../common/SimpleDialog';
import SectionAppBar from '../../v2/components/Section/SectionAppBar';
import SaveButton from '../../v2/components/Button/SaveButton';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import StandardButton from '../../v2/components/Button/StandardButton';

type Props = {
    claimType: ClaimType;
    recordId: string;
};

const RoleSelector = ({ claimType, recordId }: Props) => {
    const { options } = useOptions('roles');
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(false);
    const [selectedRecords, setSelectedRecords] = useState<RoleRecordPermissionDto[]>([]);
    const theme = useTheme();

    useEffect(() => {
        let subscribed = true;

        const loadRecordIds = async () => {
            const response = await client.get(`/api/admin/roles/claims/data/${claimType}/${recordId}`);

            if (subscribed) {
                setSelectedRecords(response.data);
            }
        };

        loadRecordIds();

        return () => {
            subscribed = false;
        };
    }, [recordId, claimType]);

    if (!options) return null;

    const handleChecked = (value: string, status: boolean) => {
        const temp = [...selectedRecords];

        const recordIndex = temp.findIndex((rp) => rp.id === value);

        if (recordIndex > -1) {
            temp[recordIndex].isActive = status;
        } else
            temp.push({
                id: value,
                claimType: claimType,
                claimValue: recordId,
                isActive: true,
            });

        setSelectedRecords(temp);
    };

    const isChecked = (value: string) => {
        return selectedRecords.some((r) => r.id === value && r.isActive);
    };

    const handleSave = async () => {
        setError(false);
        const response = await client.post<ApiResponseType>('/api/admin/roles/claims/data', { roles: selectedRecords });

        if (response.data.succeeded) {
            ApplicationStore.update((s) => {
                s.snackbarMessage = 'Role security successfully updated';
                s.snackbarOpen = true;
            });

            setOpen(false);
        } else {
            setError(true);
        }
    };

    const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(true);
    };

    return (
        <>
            <IconButton title="Authorise Roles" onClick={handleOpen}>
                <Icon>groups</Icon>
            </IconButton>

            <SimpleDialog open={open} onClose={() => setOpen(false)} fullHeight={false} padding={0}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <SectionAppBar
                        title="Authorised Roles"
                        subTitle="Please click a roles you wish to authorise."
                        buttons={
                            <>
                                <SaveButton variant="contained" size="small" onClick={handleSave} icon={<CloseIcon />}>
                                    Save
                                </SaveButton>
                                <StandardButton
                                    variant="contained"
                                    size="small"
                                    onClick={() => setOpen(false)}
                                    icon={<SaveAltIcon />}
                                >
                                    Cancel
                                </StandardButton>
                            </>
                        }
                    />
                    <Box padding={3}>
                        <TableContainer sx={{ maxHeight: '50vh', minHeight: '50vh' }}>
                            <Table size="small" sx={{ width: '100%' }} stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={2}>Roles</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {options.map((options, optionIndex: number) => {
                                        return (
                                            <TableRow
                                                key={optionIndex}
                                                sx={{ '& .MuiTableCell-root:first-of-type': { pl: 0 } }}
                                            >
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: (theme) => `${theme.palette.text.primary} !important`,
                                                    }}
                                                >
                                                    {options.name}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'left' }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={isChecked(options.value)}
                                                        onChange={(e) => handleChecked(options.value, e.target.checked)}
                                                        style={{ width: 1, whiteSpace: 'nowrap' }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {error && (
                            <Box sx={{ color: theme.palette.error.main }}>
                                An error occured while updating authorised roles.
                            </Box>
                        )}
                    </Box>
                </Box>
            </SimpleDialog>
        </>
    );
};

export default RoleSelector;
